import { useMemo } from 'react';
import { useMobileLayout } from './useMobileLayout';

export interface TouchTargetConfig {
  minSize: number;
  padding: number;
  spacing: number;
  borderRadius: number;
  feedback: {
    scale: number;
    duration: number;
    easing: string;
  };
}

export const useTouchTarget = (): TouchTargetConfig => {
  const { isMobile, isSmallMobile } = useMobileLayout();

  const config = useMemo(() => {
    if (isSmallMobile) {
      return {
        minSize: 48, // iOS recommended minimum
        padding: 12,
        spacing: 8,
        borderRadius: 8,
        feedback: {
          scale: 0.95,
          duration: 150,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        },
      };
    }
    
    if (isMobile) {
      return {
        minSize: 44, // Android recommended minimum
        padding: 10,
        spacing: 6,
        borderRadius: 6,
        feedback: {
          scale: 0.98,
          duration: 120,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        },
      };
    }

    return {
      minSize: 36, // Desktop standard
      padding: 8,
      spacing: 4,
      borderRadius: 4,
      feedback: {
        scale: 0.99,
        duration: 100,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      },
    };
  }, [isMobile, isSmallMobile]);

  return config;
};

export const getTouchTargetStyles = (config: TouchTargetConfig) => ({
  minHeight: `${config.minSize}px`,
  minWidth: `${config.minSize}px`,
  padding: `${config.padding}px`,
  borderRadius: `${config.borderRadius}px`,
  transition: `all ${config.feedback.duration}ms ${config.feedback.easing}`,
  '&:active': {
    transform: `scale(${config.feedback.scale})`,
  },
  '&:hover': {
    transform: 'translateY(-1px)',
  },
});
